import { TrashIcon } from "lucide-react";
import React, { useRef } from "react";
import { ReactComponent as GalleryIcon } from "../../assets/icons/Gallery.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/Pen.svg";

import { buildApiUrl } from "../../lib/utils";

interface FileUploadProps {
  label: string;
  accept?: string;
  value?: File | null | string;
  onChange: (file: File | null) => void;
  error?: string;
  allowedTypes?: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  label,
  accept = "image/*",
  value,
  onChange,
  error,
  allowedTypes = "png, jpg",
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    onChange(file);
  };

  const handleRemove = () => {
    onChange(null);
  };

  const handleEditClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="space-y-2">
      <label className="block text-[18px] font-semibold text-hColor">
        {label}
      </label>

      <div className="relative">
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          className="hidden"
        />

        <div
          onClick={handleEditClick}
          className={`
            relative h-[176px] border-2 border-dashed rounded-lg text-center transition-colors cursor-pointer
            ${
              error
                ? "border-red-300 bg-red-50"
                : "border-gray-300 hover:border-gray-400"
            }
          `}
        >
          {value ? (
            value instanceof File ? (
              <img
                src={URL.createObjectURL(value)}
                alt="Preview"
                className="w-full h-full object-cover rounded"
              />
            ) : typeof value === "string" ? (
              <img
                src={buildApiUrl(value)}
                alt="Preview"
                className="w-full h-full object-cover rounded"
              />
            ) : (
              <div className="flex flex-col items-center justify-center h-full space-y-2">
                <GalleryIcon className="h-[50px] w-[50px] text-gray-400" />
              </div>
            )
          ) : (
            <div className="flex flex-col items-center justify-center h-full space-y-2">
              <GalleryIcon className="h-[50px] w-[50px] text-gray-400" />
            </div>
          )}

          {/* Top-right icon */}
          <div className="absolute top-[-13px] right-[-10px] z-20">
            {value ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
                className="p-1 rounded-full bg-red-100 shadow-md hover:bg-red-200"
                aria-label="Remove file"
              >
                <TrashIcon className="h-5 w-5 text-primary" />
              </button>
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditClick();
                }}
                className="p-1 rounded-full bg-gray-50 shadow-md hover:bg-gray-100"
                aria-label="Upload file"
              >
                <EditIcon className="h-5 w-5 text-gray-500" />
              </button>
            )}
          </div>
        </div>
      </div>

      {allowedTypes && !value && (
        <p className="text-[16px] text-pColor">
          Allowed file types: {allowedTypes}
        </p>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
};
